import { FC } from "react";
import { Box } from "@mui/material";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import { useTranslation } from "react-i18next";
import { replaceDate } from "@/helper/dateHandling";
import { Link, useNavigate, useParams } from "react-router-dom";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import styles from "@/components/emailBatchStatus/emailBatchStatusTable.module.css";

const EmailBatchStatusTable: FC = () => {
  const { t } = useTranslation();
  usePageHeading(t("email_batch_status"));
  const navigate = useNavigate();

  const { batchId } = useParams() as {
    batchId?: string;
  };

  const additionalUrlParameter = batchId ? { batch_id: batchId } : undefined;

  const navigateToSubjectOverviewForCompany = (subjectId: any) => {
    const url = `/subjectOverview/${subjectId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const emailBatchStatusColumns = [
    {
      accessorKey: "id",
      header: t("id"),
      id: "id",
      enableGlobalFilter: false,
    },
    {
      accessorKey: "mailgun_id",
      header: t("mailgun_id"),
      id: "mailgun_id",
      enableGlobalFilter: false,
    },
    {
      header: t("subject"),
      id: "subject",
      accessorKey: "subject",
      customColumnFilterId: "subject_name",
      customSortingKey: "subject_name",
      Cell: ({ cell }: any) => {
        const subject = cell.getValue();
        if (!subject?.name && !subject?.email) {
          return null;
        }
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Link
              className={styles.link}
              to="/subjectOverview"
              target="_blank"
              onClick={() => navigateToSubjectOverviewForCompany(subject?.id)}
            >
              {subject?.name}
            </Link>
            {subject?.email && (
              <Box
                component="span"
                sx={{ fontSize: "0.75rem", color: "text.secondary" }}
              >
                {subject.email}
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      header: t("template"),
      id: "template",
      accessorKey: "template",
      customColumnFilterId: "template__name",
      customSortingKey: "name",
      Cell: ({ cell }: any) => {
        return <span>{cell.getValue()?.name}</span>;
      },
    },
    {
      header: t("status"),
      id: "status",
      accessorKey: "status",
      Cell: ({ cell }: any) => {
        return <>{t(`mailgun_${cell.getValue()}`)}</>;
      },
      filterVariant: "select",

      filterSelectOptions: [
        { value: "submitted", label: t("mailgun_submitted") },
        { value: "failed-temporary", label: t("mailgun_failed-temporary") },
        { value: "failed-permanent", label: t("mailgun_failed-permanent") },
        { value: "failed-mailgun", label: t("mailgun_failed-mailgun") },
        { value: "failed-rendering", label: t("mailgun_failed-rendering") },
        { value: "delivered", label: t("mailgun_delivered") },
        { value: "complained", label: t("mailgun_complained") },
      ],
    },
    {
      header: t("metadata"),
      id: "metadata",
      accessorKey: "metadata",
      enableGlobalFilter: false,
      enableSorting: false,
      Cell: ({ cell }: any) => {
        const metadata = cell.getValue();
        if (!metadata?.entity_type && !metadata?.entity_id) {
          return null;
        }
        const isAppointment =
          metadata?.entity_type === "Appointment" && metadata?.entity_id;
        const entityTypeLabel =
          metadata?.entity_type === "Appointment"
            ? t("appointment_single")
            : metadata?.entity_type;
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            {isAppointment ? (
              <Box
                component={Link}
                to={`/appointmentDashboard/${metadata.entity_id}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontWeight: 600,
                  color: "#8c1ec8",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {entityTypeLabel}
              </Box>
            ) : (
              <Box component="span" sx={{ fontWeight: 600 }}>
                {entityTypeLabel ?? "—"}
              </Box>
            )}
            {metadata?.entity_id && (
              <Box
                component="span"
                sx={{ fontSize: "0.75rem", color: "text.secondary" }}
              >
                {metadata.entity_id}
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      header: t("maillog_sent_on"),
      accessorKey: "sent_on",
      id: "sent_on",
      sortingFn: "datetime",
      filterVariant: "date",
      Cell: ({ cell }: any) => {
        return <span>{replaceDate(cell.getValue(), true, true)}</span>;
      },
    },
    {
      header: t("modified_on"),
      accessorKey: "modified_on",
      id: "modified_on",
      sortingFn: "datetime",
      filterVariant: "date",
      Cell: ({ cell }: any) => {
        return <span>{replaceDate(cell.getValue(), true, true)}</span>;
      },
    },
    ...getRecordMetadataColumns(t, ["created_on", "created_by", "modified_by"]),
  ];
  return (
    <>
      {batchId && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          <span>
            {t("email_batch_status_filtered_by_batch")}:{" "}
            <strong>{batchId}</strong>
          </span>
          <GenericButton
            variant="outlined"
            color="tertiary"
            onClick={() => navigate("/settings/emailstatus")}
          >
            {t("email_batch_status_show_all")}
          </GenericButton>
        </Box>
      )}
      <GenericCrudTable
        entity="mailLog"
        columns={emailBatchStatusColumns}
        additionalUrlParameter={additionalUrlParameter}
        readOnlyMode
        showColumnFilterbyDefault
        customColumnVisibility={{ mailgun_id: false, modified_on: true }}
      />
    </>
  );
};

export default EmailBatchStatusTable;
